import { getFieldValue, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useRouter } from 'next/router';
import React from 'react';
import classNames from 'classnames/bind';
import withStyles from 'lib/enhancers/withStyles';
import compose from 'lib/enhancers/compose';
import styles from './EpisodeDetail.module.scss';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import Button from 'components/helpers/Button/Button';
import { ButtonVariant } from 'components/helpers/Button/Button';
import { useDictionary } from 'lib/utils';

import ShowSummaryWithLinks from './ShowSummaryWithLinks';

import type { RecordOfFields } from 'models/fields';
import type { EpisodeDetailProps } from 'models/podcasts-types';
import SeasonEpisodes from './SeasonEpisodes';
import Hosts from './Hosts';
import Icon from 'components/helpers/Icon';

const cx = classNames.bind(styles);

const EpisodeDetail = (props: EpisodeDetailProps): React.JSX.Element => {
  const { className, fields } = props;
  const { description, showDetails, episodes, transcriptPageUrl, guests, kalturaMediaID, relatedToThisEpisode } =
    fields;
  const router = useRouter();
  const { sitecoreContext } = useSitecoreContext();

  const titleHeadingLevelItemField = fields.titleHeadingLevel?.fields as RecordOfFields;
  const titleHeadingLevelValue = getFieldValue<string>(titleHeadingLevelItemField, 'Value') || 'h2';
  const titleTextStyleItemField = fields.titleTextStyle?.fields as RecordOfFields;
  const titleTextStyleValue = getFieldValue<string>(titleTextStyleItemField, 'Value') || 'font-style-xl';

  const kalturaCode = kalturaMediaID.value;
  const kalturaPlayerTypeItemField = fields.kalturaPlayerType?.fields as RecordOfFields;
  const kalturaPlayerTypeValue = getFieldValue<string>(kalturaPlayerTypeItemField, 'playerID') || '';
  const kalturaPlayerIsVideo = getFieldValue<boolean>(kalturaPlayerTypeItemField, 'isVideoPlayer');
  const playerId = 'video_id_' + kalturaCode;

  const hostsTextPlural = useDictionary('Guests');
  const hostsTextSingular = useDictionary('Guest');
  const titleOverride = useDictionary('More Episodes');
  const viewTranscript = useDictionary('View Transcript');
  const relatedToThisEpisodeHeading = useDictionary('Related to this episode');

  if (typeof window !== 'undefined' && kalturaCode != '') {
    const scriptk = document.createElement('script');

    scriptk.src =
      'https://cdnapisec.kaltura.com/p/6105852/embedPlaykitJs/uiconf_id/' +
      kalturaPlayerTypeValue +
      '?autoembed=true&targetId=' +
      playerId +
      '&entry_id=' +
      kalturaCode;

    scriptk.type = 'text/javascript';
    scriptk.async = true;

    const existing = document.querySelector(`script[src="${scriptk.src}"]`);
    if (existing) {
      existing.remove();
    }

    document.body.appendChild(scriptk);
  }

  return (
    <div className={cx('podcasts-season-details', className)}>
      <div className={cx('podcasts-show-synopsis', className)}>
        {sitecoreContext.pageEditing ? (
          <Text field={description} tag="div" encode={sitecoreContext.pageEditing} />
        ) : (
          <div
            dangerouslySetInnerHTML={{
              __html: '<p>' + String(description.value)?.replace(/\r\n/g, '</p><p>') + '</p>',
            }}
          />
        )}
        {transcriptPageUrl != '' && (
          <p>
            <Button
              type="submit"
              variant={ButtonVariant.Primary}
              className={cx('loginButton', 'view-transcript-button')}
              onClick={() => {
                router.push(transcriptPageUrl);
              }}
            >
              <Icon variant="list" />
              {viewTranscript}
            </Button>
          </p>
        )}
        <div>
          {kalturaCode != '' && (
            <div
              className={cx('responsive_padding', kalturaPlayerIsVideo ? 'video-padding' : 'audio-padding', className)}
            >
              <div className={cx('responsive_wrapper', 'video-wrapper')}>
                <div id={playerId} style={{ width: '100%', height: '100%' }}></div>
              </div>
            </div>
          )}
        </div>
        {relatedToThisEpisode.value && (
          <>
            <Text field={{ value: relatedToThisEpisodeHeading }} tag="h2" className={cx({ title: true })} />
            <RichTextWrapper field={relatedToThisEpisode} className={cx('related-to-text')} tag="div" />
          </>
        )}
        <ShowSummaryWithLinks showDetails={showDetails} />
      </div>

      <Hosts
        hosts={guests}
        personFallbackImage={showDetails.personFallbackImage}
        hostsTextPluralOverride={hostsTextPlural}
        hostsTextSingularOverride={hostsTextSingular}
      />

      {episodes && episodes.length > 0 && (
        <SeasonEpisodes
          episodes={episodes}
          episodesFallbackImage={showDetails.fallbackImage}
          titleHeadingLevel={titleHeadingLevelValue}
          titleTextStyle={titleTextStyleValue}
          titleOverride={titleOverride}
          episdoesToShowInitially={2}
        />
      )}
    </div>
  );
};

export default compose<EpisodeDetailProps>(withStyles())(EpisodeDetail);
